import React from 'react';
import { Button } from '@progress/kendo-react-buttons';
import { Switch } from '@progress/kendo-react-inputs';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck, faTimes, faExpand } from '@fortawesome/free-solid-svg-icons';
import CodeMirror from '@uiw/react-codemirror';
import { useAppFlowEditor } from './../../Pages/AppFlowEditorProvider';

/**
 * Component to view Custom Data Value Cell
 * @param {Object} props - Component properties
 * @returns {JSX.Element} React component for App Flow Detail Argument
 */
const CustomDataValueCell = (props) => {
  const { dataItem, field, onChange } = props;
  const { setEditorField, setShowModal } = useAppFlowEditor();

  /**
   * editor value change
   */
  const onEditorValChange = React.useCallback(
    (val) => {
      onChange({
        dataItem: dataItem,
        field: field,
        value: val,
      });
    },
    [dataItem],
  );
  /**
   * open the editor in full screen mode
   */
  const clickOpenButton = () => {
    setEditorField({
      name: field,
      value: dataItem.UcOssiDataVal,
      label: 'Data Value',
      title: 'Data Value',
      row_no: dataItem?.row_no,
    });
    setShowModal(true);
  };

  if (dataItem.inEdit) {
    return (
      <td>
        <div className='data-field-cell'>
          <CodeMirror
            value={dataItem.UcOssiDataVal ?? ''}
            height='5vh'
            onChange={onEditorValChange}
          />
          <span onClick={clickOpenButton} className='open-editor-icon'>
            <FontAwesomeIcon
              className='icon-full-screen k-mr-1'
              icon={faExpand}
            />
          </span>
        </div>
      </td>
    );
  }
  return <td>{dataItem.UcOssiDataVal}</td>;
};

export const DataColumns = (
  editID,
  argumentData,
  saveRow,
  cancelEdit,
  setEditRow,
  showDeleteDialog,
) => [
  {
    field: 'UcOssiAfStepArgId',
    title: 'AF Step Argument Id*',
    width: '200px',
    minResizableWidth: 200,
    editable: editID === null || argumentData.some((item) => item.isNew),
  },
  {
    field: 'UcOssiDescr',
    title: 'Description',
    width: '300px',
    minResizableWidth: 250,
    editable: true,
  },
  {
    field: 'UcOssiDataVal',
    title: 'Data Value*',
    width: '400px',
    minResizableWidth: 200,
    editable: true,
    cell: CustomDataValueCell,
  },
  {
    field: 'UcOssiUseStackVarFlg',
    title: 'Stack Variable*',
    width: '200px',
    minResizableWidth: 200,
    cell: (props) => (
      <td>
        {props.dataItem.inEdit ? (
          <Switch
            checked={props.dataItem.UcOssiUseStackVarFlg === 1}
            onChange={(e) =>
              props.onChange({
                dataItem: props.dataItem,
                field: props.field,
                value: e.value ? 1 : 0,
              })
            }
          />
        ) : props.dataItem.UcOssiUseStackVarFlg === 1 ? (
          <FontAwesomeIcon icon={faCheck} className='grid-flag-icon' />
        ) : (
          <FontAwesomeIcon icon={faTimes} className='grid-flag-icon' />
        )}
      </td>
    ),
  },
  {
    width: '300',
    minResizableWidth: 300,
    title: 'Actions',
    cell: (props) => (
      <td>
        {props.dataItem.inEdit ? (
          <>
            <Button
              themeColor='primary'
              icon={'save'}
              size='small'
              fillMode={'flat'}
              onClick={() => saveRow(props.dataItem)}
              className='k-button k-mr-2'
            ></Button>
            <Button
              themeColor='light'
              size='small'
              icon={'cancel'}
              fillMode={'solid'}
              onClick={() => cancelEdit(props.dataItem.row_no)}
              className='k-button'
            ></Button>
          </>
        ) : (
          <>
            <Button
              icon={'pencil'}
              themeColor='primary'
              size='small'
              fillMode={'flat'}
              onClick={() => setEditRow(props.dataItem.row_no)}
              className='k-button k-mr-2 k-font-size-sm'
            ></Button>
            <Button
              themeColor='error'
              size='small'
              icon={'trash'}
              fillMode={'flat'}
              onClick={() => showDeleteDialog(props.dataItem)}
            ></Button>
          </>
        )}
      </td>
    ),
  },
];
